import React, { useEffect, useRef, useState } from "react";
import {
    FaDownload,
    FaFileAlt,
    FaFilePdf,
    FaFileExcel,
    FaFileWord,
    FaImage,
    FaTimes,
    FaRedo,
} from "react-icons/fa";
import { getWhatsappMediaSrc } from "../../features/message/Messageservice";

export type MediaKind = "image" | "video" | "audio" | "document";

export interface ChatMediaInfo {
    kind: MediaKind;
    mediaId?: string | null;
    mediaUrl?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
}

/** Decide how to show a message from messageType + mimeType */
export const getMediaKind = (
    messageType?: string | null,
    mimeType?: string | null
): MediaKind | null => {
    const type = (messageType || "").toLowerCase();
    const mime = (mimeType || "").toLowerCase();

    if (type === "image" || type === "sticker" || (type === "document" && mime.startsWith("image/")))
        return "image";
    if (type === "video") return "video";
    if (type === "audio" || type === "voice") return "audio";
    if (type === "document") return "document";
    return null;
};

/** Loads the media src when the message scrolls into view */
function useMediaSrc(media: ChatMediaInfo, autoLoad: boolean) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [src, setSrc] = useState<string | null>(media.mediaUrl || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const load = async () => {
        if (src) return src;
        if (!media.mediaId) {
            setError(true);
            return null;
        }
        setLoading(true);
        setError(false);
        try {
            const url = await getWhatsappMediaSrc(media.mediaId);
            setSrc(url);
            return url;
        } catch (e) {
            console.error("Failed to load media", e);
            setError(true);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!autoLoad || src || !ref.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    observer.disconnect();
                    load();
                }
            },
            { rootMargin: "200px" }
        );

        observer.observe(ref.current);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media.mediaId, autoLoad]);

    return { ref, src, loading, error, load };
}

const fileIcon = (name?: string | null, mime?: string | null) => {
    const n = `${name || ""} ${mime || ""}`.toLowerCase();
    if (n.includes("pdf")) return <FaFilePdf className="text-[#e5252a]" />;
    if (n.includes("sheet") || n.includes("excel") || /\.(xlsx?|csv)/.test(n))
        return <FaFileExcel className="text-[#1d6f42]" />;
    if (n.includes("word") || /\.docx?/.test(n)) return <FaFileWord className="text-[#2b579a]" />;
    return <FaFileAlt className="text-[#8696a0]" />;
};

const extension = (name?: string | null, mime?: string | null) => {
    const fromName = name?.split(".").pop();
    if (fromName && fromName !== name) return fromName.toUpperCase();
    return (mime?.split("/").pop() || "FILE").toUpperCase();
};

const downloadFile = (url: string, fileName?: string | null) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "file";
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export default function ChatMedia({ media }: { media: ChatMediaInfo }) {
    // Images/video/audio load when visible; documents only when downloaded
    const { ref, src, loading, error, load } = useMediaSrc(media, media.kind !== "document");
    const [preview, setPreview] = useState(false);

    // Close full-screen preview with Esc
    useEffect(() => {
        if (!preview) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setPreview(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [preview]);

    const retry = (
        <button
            type="button"
            onClick={load}
            className="flex flex-col items-center gap-1 text-[12px] text-[#54656f]"
        >
            <FaRedo />
            Retry
        </button>
    );

    /* ---------- Image ---------- */
    if (media.kind === "image") {
        return (
            <>
                <div
                    ref={ref}
                    className="w-[240px] h-[240px] max-w-full rounded-md overflow-hidden bg-[#e9edef] flex items-center justify-center mb-1"
                >
                    {src ? (
                        <img
                            src={src}
                            alt={media.fileName || "Image"}
                            onClick={() => setPreview(true)}
                            className="w-full h-full object-cover cursor-pointer"
                        />
                    ) : error ? (
                        retry
                    ) : (
                        <FaImage
                            className={`text-[32px] text-[#c4cbd0] ${loading ? "animate-pulse" : ""}`}
                        />
                    )}
                </div>

                {preview && src && (
                    <div
                        className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-6"
                        onClick={() => setPreview(false)}
                    >
                        <div className="absolute top-4 right-4 flex gap-2">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    downloadFile(src, media.fileName);
                                }}
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                aria-label="Download"
                            >
                                <FaDownload />
                            </button>
                            <button
                                type="button"
                                onClick={() => setPreview(false)}
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <img
                            src={src}
                            alt={media.fileName || "Image"}
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-full max-h-full object-contain rounded"
                        />
                    </div>
                )}
            </>
        );
    }

    /* ---------- Video ---------- */
    if (media.kind === "video") {
        return (
            <div
                ref={ref}
                className="w-[260px] h-[180px] max-w-full rounded-md overflow-hidden bg-black/80 flex items-center justify-center mb-1"
            >
                {src ? (
                    <video src={src} controls preload="metadata" className="w-full h-full object-contain" />
                ) : error ? (
                    retry
                ) : (
                    <span className="w-6 h-6 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
                )}
            </div>
        );
    }

    /* ---------- Audio / voice note ---------- */
    if (media.kind === "audio") {
        return (
            <div ref={ref} className="w-[260px] max-w-full h-[54px] flex items-center mb-1">
                {src ? (
                    <audio src={src} controls preload="metadata" className="w-full h-10" />
                ) : error ? (
                    retry
                ) : (
                    <div className="w-full h-10 rounded-full bg-black/5 animate-pulse" />
                )}
            </div>
        );
    }

    /* ---------- Document ---------- */
    return (
        <div
            ref={ref}
            className="w-[260px] max-w-full flex items-center gap-3 rounded-md bg-black/[0.04] px-3 py-2.5 mb-1"
        >
            <div className="text-[28px] flex-shrink-0">{fileIcon(media.fileName, media.mimeType)}</div>

            <div className="min-w-0 flex-1">
                <p className="text-[14px] text-[#111b21] truncate">{media.fileName || "Document"}</p>
                <p className="text-[11px] text-[#667781] mt-0.5">
                    {error ? "Couldn't load file" : extension(media.fileName, media.mimeType)}
                </p>
            </div>

            <button
                type="button"
                disabled={loading}
                onClick={async () => {
                    const url = src || (await load());
                    if (url) downloadFile(url, media.fileName);
                }}
                className="w-9 h-9 flex-shrink-0 rounded-full border border-[#8696a0]/50 text-[#54656f] flex items-center justify-center hover:bg-black/5 disabled:opacity-60"
                aria-label="Download"
            >
                {loading ? (
                    <span className="w-4 h-4 rounded-full border-2 border-[#54656f] border-t-transparent animate-spin" />
                ) : (
                    <FaDownload className="text-[13px]" />
                )}
            </button>
        </div>
    );
}
